import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { DamagesService } from '../damages.service';
import {
  createIncident,
  createIncidentFailure,
  createIncidentSuccess,
  getAvailableSurveyPointsForVIN,
  getDamageHistoriesFailure,
  getDamageHistoriesSuccess,
  getSurveyPointsForVINFailure,
  getSurveyPointsForVINSuccess,
  loadDamageHistories,
  loadIncidentsFailed,
  loadReturnDamagesFailed,
  loadSurveyPointFailed,
  loadTransportStagesFailed,
  loadVehicleFailed,
  loadVehicleOptionsFailed,
  setExistingDamages,
  setSurveyPoint,
  startNewDamageEntryProcess,
  startNewDamageEntryProcessCompleted,
  startNewDamageEntryProcessFailed,
} from './damages.actions';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  IDamageRequest,
  IDamageResponse,
  ITransportStage,
  IVehicle,
  IVehicleOptionOptions,
} from '../damages.models';
import { LocationsService } from '../../admin/locations/locations.service';
import { LiabilitiesService } from '../../liabilities/liabilities.service';
import { ISurveyPoint } from '../../admin/locations/locations.models';

@Injectable()
export class DamageEntryEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(DamagesService);
  private readonly liabilitiesService = inject(LiabilitiesService);
  private readonly locationsService = inject(LocationsService);
  private readonly store = inject(Store);

  public startNewDamageEntryProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startNewDamageEntryProcess),
      switchMap(({ vehicle, surveyPoint }) => {
        const vin = vehicle.vin17 as string;

        // 1. Load survey point
        const surveyPoint$ = this.locationsService
          .getSurveyPointById(surveyPoint.id!)
          .pipe(
            map((i) => ({
              ...i,
              locationEntryDate: surveyPoint.locationEntryDate,
            })),
            take(1),
            catchError((error) => {
              return of(null).pipe(
                tap(() => this.store.dispatch(loadSurveyPointFailed({ error })))
              );
            })
          );

        // 2. Load transport stages
        const transportStages$ = this.service
          .getTransportCausers(vin, surveyPoint as ISurveyPoint)
          .pipe(
            catchError((error) => {
              return of([] as ITransportStage[]).pipe(
                tap(() =>
                  this.store.dispatch(loadTransportStagesFailed({ error }))
                )
              );
            })
          );

        // 3. Load vehicle
        const vehicle$ = this.service.getVehicleByVIN(vin).pipe(
          catchError((error) => {
            return of(null as IVehicle | null).pipe(
              tap(() => this.store.dispatch(loadVehicleFailed({ error })))
            );
          })
        );

        // 4. From vehicle, load options
        const vehicleWithOptions$ = vehicle$.pipe(
          switchMap((v) => {
            if (!v) return of({ vehicle: null, options: [] as string[] });
            return this.service.getVehicleOptions(v.carBrand, v.modelCode).pipe(
              map((opt) => ({ vehicle: v, options: opt.options })),
              catchError((error) => {
                this.store.dispatch(loadVehicleOptionsFailed({ error }));
                return of({ vehicle: v, options: [] as string[] });
              })
            );
          })
        );

        // 5. Load existing incidents
        const existingIncidents$ = this.service.getIncidentsByVin(vin).pipe(
          catchError((error) => {
            return of([]).pipe(
              tap(() => this.store.dispatch(loadIncidentsFailed({ error })))
            );
          })
        );

        // 6. Load return damages
        const returnDamages$ = this.service.getReturnDamages(vin).pipe(
          catchError((error) => {
            return of([]).pipe(
              tap(() => this.store.dispatch(loadReturnDamagesFailed({ error })))
            );
          })
        );

        // forkJoin all of them
        return forkJoin({
          surveyPoint: surveyPoint$,
          transportStages: transportStages$,
          vehicleData: vehicleWithOptions$,
          existingDamages: existingIncidents$,
          returnDamages: returnDamages$,
        }).pipe(
          // 6. Push the survey point and existing damages into the store immediately
          tap(({ surveyPoint, existingDamages }) => {
            if (surveyPoint) {
              this.store.dispatch(setSurveyPoint({ surveyPoint }));
            }
            this.store.dispatch(setExistingDamages({ existingDamages }));
          }),
          // 7. Emit success if at least vehicleData.vehicle is present
          map(
            ({
              surveyPoint,
              transportStages,
              vehicleData,
              existingDamages,
              returnDamages,
            }) =>
              startNewDamageEntryProcessCompleted({
                surveyPoint: surveyPoint!,
                transportStages,
                vehicleFromApi: vehicleData.vehicle!,
                vehicleOptions: vehicleData.options as IVehicleOptionOptions[],
                existingDamages,
                returnDamages,
              })
          ),
          // 8. Just in case forkJoin itself errors
          catchError((error) => of(startNewDamageEntryProcessFailed({ error })))
        );
      })
    )
  );

  public getSurveyPointsByVIN$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAvailableSurveyPointsForVIN),
      switchMap(({ vin }) =>
        this.service.getSurveyPoints(vin).pipe(
          map((availableSurveyPoints) => {
            return getSurveyPointsForVINSuccess({ availableSurveyPoints });
          }),
          catchError((err) => of(getSurveyPointsForVINFailure({ error: err })))
        )
      )
    )
  );

  public submitIncident$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createIncident),
      switchMap(({ incident }) => {
        const { newDamages, ...incidentData } = incident;
        return this.service.createIncident(incidentData).pipe(
          switchMap((incidentResponse) => {
            const incidentId = incidentResponse.id;
            const damageObservables = newDamages.map((damage) =>
              this.createDamageWithFiles(damage, incidentId)
            );
            if (damageObservables.length === 0) {
              return this.service.finalizeIncident(incidentId).pipe(
                map(() => createIncidentSuccess()),
                catchError((error) => of(createIncidentFailure({ error })))
              );
            }
            return forkJoin(damageObservables).pipe(
              switchMap(() => this.service.finalizeIncident(incidentId)),
              map(() => {
                return createIncidentSuccess();
              }),
              catchError((error) => of(createIncidentFailure({ error })))
            );
          }),
          catchError((error) => of(createIncidentFailure({ error })))
        );
      })
    )
  );

  private createDamageWithFiles(damage: IDamageRequest, incidentId: number) {
    const fileRequests = damage.files || [];
    const damagePayload = { ...damage, incidentId };
    return this.service.createDamage(damagePayload).pipe(
      switchMap((damageResponse: IDamageResponse) =>
        this.uploadDamageFiles(damageResponse, fileRequests)
      ),
      catchError((error) => of({ failed: true, error }))
    );
  }

  private uploadDamageFiles(
    damageResponse: IDamageResponse,
    fileRequests: IDamageRequest['files']
  ): Observable<IDamageResponse | { failed: boolean; error: unknown }> {
    if (
      !fileRequests?.length ||
      damageResponse.files?.length !== fileRequests.length
    ) {
      return of(damageResponse);
    }
    const uploadObservables = fileRequests.map((fileReq) =>
      this.uploadSingleFile(fileReq, damageResponse)
    );
    return forkJoin(uploadObservables).pipe(map(() => damageResponse));
  }

  private uploadSingleFile(
    fileReq: NonNullable<IDamageRequest['files']>[number],
    damageResponse: IDamageResponse
  ) {
    const fileMeta = damageResponse.files.find(
      (file) => file.fileName === fileReq.file.name
    );
    if (!fileMeta) {
      console.error(
        'Could not find matching file metadata for:',
        fileReq.file.name
      );
      return of({
        failed: true,
        error: new Error(
          `No matching file metadata found for ${fileReq.file.name}`
        ),
      });
    }
    return this.service.uploadFile(fileReq.file, fileMeta.preSignedUrl).pipe(
      catchError((error) => {
        console.error('File upload failed:', fileReq.file.name, error);
        return of({ failed: true, error });
      })
    );
  }

  public getDamageHistories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDamageHistories),
      switchMap(({ vin17 }) =>
        this.liabilitiesService.getDamageHistories(vin17).pipe(
          map((damageHistories) =>
            getDamageHistoriesSuccess({ damageHistories })
          ),
          catchError((error) => of(getDamageHistoriesFailure({ error })))
        )
      )
    )
  );
}
